package org.moxultimate.topwithmox.showscrud.api

import io.ktor.client.*
import io.ktor.client.request.*
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.serialization.json.Json
import org.khronos.webgl.Int8Array
import org.moxultimate.topwithmox.shows.data.ShowsResponse
import org.moxultimate.topwithmox.shows.data.ShowsTagResponse
import org.moxultimate.topwithmox.shows.data.ShowsTypeResponse
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState
import org.w3c.files.Blob
import org.w3c.xhr.FormData

/**
 * ShowsCRUDRepository
 * Handles all write operations for shows: create, update, delete, and saving tags/types.
 * Read operations live in ShowsRepository to keep concerns separate.
 *
 * @param httpClient Ktor HTTP client used for POST, PUT, and DELETE requests.
 */
class ShowsCRUDRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/shows"
    private val tagUrl  = "$baseAPI/shows-tag"
    private val typeUrl = "$baseAPI/shows-type"

    /**
     * createShow
     * Creates a new show entry via POST /shows (multipart/form-data).
     * Uses browser FormData + fetch() — Ktor's multipart builder generates Content-Disposition
     * headers that Spring 6 rejects.
     *
     * @param title Show title.
     * @param type Show type (Anime, Movies, Educational, Productivity, Series).
     * @param tags Comma-separated tag string (optional).
     * @param description Show description.
     * @param videoLink YouTube or external video URL string.
     * @param imageBytes Raw bytes of the thumbnail image to upload.
     * @return Flow emitting Loading → Success(ShowsResponse) or Error.
     */
    fun createShow(
        title: String,
        type: String,
        tags: String,
        description: String,
        videoLink: String,
        imageBytes: ByteArray,
    ): Flow<APIState<ShowsResponse>> = flow {
        emit(APIState.Loading)
        emit(performCreateShow(title, type, tags, description, videoLink, imageBytes))
    }

    private suspend fun performCreateShow(
        title: String,
        type: String,
        tags: String,
        description: String,
        videoLink: String,
        imageBytes: ByteArray,
    ): APIState<ShowsResponse> {
        return try {
            val form = FormData()
            form.append("title", title)
            form.append("type", type)
            if (tags.isNotBlank()) form.append("tags", tags)
            form.append("description", description)
            if (videoLink.isNotBlank()) form.append("video_link", videoLink)
            val imageBlob = Blob(arrayOf(imageBytes.unsafeCast<Int8Array>().buffer), imageType)
            form.append("image", imageBlob, "image.webp")

            val init: dynamic = js("{}")
            init.method = "POST"
            init.body = form
            val response = window.fetch(baseUrl, init).await()
            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }
            APIState.Success(lenientJson.decodeFromString<ShowsResponse>(response.text().await()))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Create failed")
        }
    }

    /**
     * updateShow
     * Updates an existing show via PUT /shows/{id} (multipart/form-data, all fields optional).
     * Only non-null, non-blank fields and a provided image are sent.
     *
     * @param id Show ID to update.
     * @param title Updated title, or null to leave unchanged.
     * @param type Updated type, or null to leave unchanged.
     * @param tags Updated tags string, or null to leave unchanged.
     * @param description Updated description, or null to leave unchanged.
     * @param videoLink Updated video URL string, or null to leave unchanged.
     * @param imageBytes New image bytes, or null to keep existing.
     * @return Flow emitting Loading → Success(ShowsResponse) or Error.
     */
    fun updateShow(
        id: Long,
        title: String? = null,
        type: String? = null,
        tags: String? = null,
        description: String? = null,
        videoLink: String? = null,
        imageBytes: ByteArray? = null,
    ): Flow<APIState<ShowsResponse>> = flow {
        emit(APIState.Loading)
        emit(performUpdateShow(id, title, type, tags, description, videoLink, imageBytes))
    }

    private suspend fun performUpdateShow(
        id: Long,
        title: String?,
        type: String?,
        tags: String?,
        description: String?,
        videoLink: String?,
        imageBytes: ByteArray?,
    ): APIState<ShowsResponse> {
        return try {
            val form = FormData()
            title?.takeIf { it.isNotBlank() }?.let { form.append("title", it) }
            type?.takeIf { it.isNotBlank() }?.let { form.append("type", it) }
            tags?.let { form.append("tags", it) }
            description?.takeIf { it.isNotBlank() }?.let { form.append("description", it) }
            videoLink?.takeIf { it.isNotBlank() }?.let { form.append("video_link", it) }
            imageBytes?.let { bytes ->
                val blob = Blob(arrayOf(bytes.unsafeCast<Int8Array>().buffer), imageType)
                form.append("image", blob, "image.webp")
            }

            val init: dynamic = js("{}")
            init.method = "PUT"
            init.body = form
            val response = window.fetch("$baseUrl/$id", init).await()
            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }
            APIState.Success(lenientJson.decodeFromString<ShowsResponse>(response.text().await()))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Update failed")
        }
    }

    /**
     * deleteShow
     * Deletes a show entry and its S3 files via DELETE /shows/{id}.
     *
     * @param id Show ID to delete.
     * @return Flow emitting Loading → Success or Error.
     */
    fun deleteShow(id: Long): Flow<APIState<String>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall { httpClient.delete("$baseUrl/$id") })
    }

    /**
     * saveTag
     * Registers a new tag via POST /shows-tag?tag=String (idempotent).
     *
     * @param tag Tag label to persist.
     * @return Flow emitting Loading → Success(ShowsTagResponse) or Error.
     */
    fun saveTag(tag: String): Flow<APIState<ShowsTagResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.post(tagUrl) { parameter("tag", tag) }
        })
    }

    /**
     * saveType
     * Registers a new type via POST /shows-type?type=String (idempotent).
     *
     * @param type Type label to persist.
     * @return Flow emitting Loading → Success(ShowsTypeResponse) or Error.
     */
    fun saveType(type: String): Flow<APIState<ShowsTypeResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.post(typeUrl) { parameter("type", type) }
        })
    }
}

/** Lenient JSON parser — tolerates extra fields returned by the server. */
private val lenientJson = Json { ignoreUnknownKeys = true }

/** MIME type options for thumbnail images. */
private val imageType: dynamic = js("{type: 'image/webp'}")
